/**
 * Staff-Member Messaging Service
 *
 * Handles staff-member conversation creation and management using the existing messaging infrastructure.
 * Creates group conversations with [Member + All Club Staff] but presents them as 1-on-1 chats to members.
 *
 * Description format: "staff_support:club:{clubId}" for exact matching
 * Title format: "{ClubName} Support"
 */

import { MessagingService } from './messaging.service';
import { BaseService } from './base.service';
import type { Conversation, DirectMessage } from './messaging.service';

export class StaffMemberMessagingService extends BaseService {
  private messagingService = MessagingService.getInstance();

  /**
   * Find existing staff conversation using direct query (no loops)
   */
  private async findStaffConversation(memberUserId: string, clubId: string): Promise<Conversation | null> {
    // Query 1: Get user's conversation IDs
    const { data: participantConvs, error: participantError } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', memberUserId)
      .eq('is_active', true);

    if (participantError || !participantConvs?.length) {
      return null;
    }

    const conversationIds = participantConvs.map(p => p.conversation_id);

    // Query 2: Find conversation with exact description match
    const { data: conversation, error: convError } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .eq('description', `staff_support:club:${clubId}`)
      .limit(1)
      .maybeSingle();

    if (convError || !conversation) {
      return null;
    }

    return conversation as Conversation;
  }

  /**
   * Get club name by ID
   */
  private async getClubName(clubId: string): Promise<string> {
    const { data, error } = await this.supabase
      .from('clubs')
      .select('name')
      .eq('id', clubId)
      .single();

    if (error || !data) {
      return 'Club';
    }
    return data.name;
  }

  /**
   * Find existing Strokone support conversation.
   * Uses a single join: find conversations where BOTH the member and Strokone are participants.
   */
  private async findStrokonSupportConversation(memberUserId: string): Promise<Conversation | null> {
    const STROKON_USER_ID = '83f0046e-269c-4863-9f68-1fc753996bc4';

    // Single query: get conversations the member is in that also have Strokone as participant.
    // We join via conversation_participants twice — first for the member's side, then we filter
    // on description to skip the N+1 participant loop entirely.
    const { data: memberConvs, error } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id, conversations!inner(id, description, conversation_type, title, last_message, last_message_at, created_at, updated_at, created_by, avatar_url)')
      .eq('user_id', memberUserId)
      .eq('is_active', true)
      .eq('conversations.conversation_type', 'direct')
      .eq('conversations.description', 'strokon_support:founder');

    if (error || !memberConvs?.length) {
      // Fallback: also check legacy conversations (null description) via the Strokone participant
      return this.findStrokonSupportConversationLegacy(memberUserId, STROKON_USER_ID);
    }

    // Verify Strokone is also a participant in the matched conversation
    const conv = memberConvs[0]?.conversations as any;
    if (!conv) return this.findStrokonSupportConversationLegacy(memberUserId, STROKON_USER_ID);

    const { data: strokonParticipant } = await this.supabase
      .from('conversation_participants')
      .select('user_id')
      .eq('conversation_id', conv.id)
      .eq('user_id', STROKON_USER_ID)
      .eq('is_active', true)
      .maybeSingle();

    return strokonParticipant ? (conv as Conversation) : null;
  }

  /**
   * Fallback for legacy Strokone conversations without description tag.
   * Only called when no description-tagged conversation exists (rare path).
   */
  private async findStrokonSupportConversationLegacy(memberUserId: string, strokonUserId: string): Promise<Conversation | null> {
    // Find conversations where Strokone is a participant
    const { data: strokonConvs } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', strokonUserId)
      .eq('is_active', true);

    if (!strokonConvs?.length) return null;

    const strokonConvIds = strokonConvs.map(p => p.conversation_id);

    // Find the intersection: conversations where the member is also a participant
    const { data: memberConvs } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', memberUserId)
      .eq('is_active', true)
      .in('conversation_id', strokonConvIds);

    if (!memberConvs?.length) return null;

    const sharedConvIds = memberConvs.map(p => p.conversation_id);

    const { data: conv } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', sharedConvIds)
      .eq('conversation_type', 'direct')
      .is('description', null)
      .limit(1)
      .maybeSingle();

    return conv ? (conv as Conversation) : null;
  }

  /**
   * Get or create Strokone support conversation (founder contact)
   * Strokone user ID: 83f0046e-269c-4863-9f68-1fc753996bc4
   */
  async getOrCreateStrokonSupportConversation(memberUserId: string): Promise<string> {
    const STROKON_USER_ID = '83f0046e-269c-4863-9f68-1fc753996bc4';
    
    // Check if conversation already exists
    const existingConv = await this.findStrokonSupportConversation(memberUserId);

    if (existingConv) {
      return existingConv.id;
    }

    // Create new Strokone support conversation (direct message)
    const result = await this.messagingService.createConversation({
      conversation_type: 'direct',
      participant_ids: [STROKON_USER_ID],
      title: 'Strokone Support',
      description: 'strokon_support:founder'
    });

    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to create Strokone support conversation');
    }

    return result.data?.data?.id || result.data?.id;
  }

  /**
   * Get Strokone support conversation if it exists
   */
  async getStrokonSupportConversation(memberUserId: string): Promise<Conversation | null> {
    return this.findStrokonSupportConversation(memberUserId);
  }

  /**
   * Get or create staff conversation for a member in a specific club
   * Uses direct query for finding existing conversation (O(1) instead of O(n))
   */
  async getOrCreateStaffConversation(memberUserId: string, clubId: string): Promise<string> {
    // Check if conversation already exists using direct query
    const existingConv = await this.findStaffConversation(memberUserId, clubId);

    if (existingConv) {
      return existingConv.id;
    }

    // Create new conversation with all club staff
    const staffUserIds = await this.getClubStaffUserIds(clubId);

    if (staffUserIds.length === 0) {
      throw new Error('No staff members found for this club');
    }

    // Get current user to avoid duplicate entries
    const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get club name for title
    const clubName = await this.getClubName(clubId);

    // Remove current user from participant_ids to avoid duplicates
    // (createConversation automatically adds the current user as admin)
    const participantIds = [memberUserId, ...staffUserIds].filter(id => id !== user.id);

    const result = await this.messagingService.createConversation({
      conversation_type: 'group',
      participant_ids: participantIds,
      title: `${clubName} Support`,
      description: `staff_support:club:${clubId}` // Machine-readable for exact matching
    });

    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to create staff conversation');
    }

    const conversationId = result.data?.data?.id;
    return conversationId;
  }

  /**
   * Send message from member to staff
   */
  async sendMemberMessage(data: {
    memberUserId: string;
    clubId: string;
    content: string;
  }): Promise<DirectMessage> {
    return this.withErrorHandling(async () => {
      // Get or create the staff conversation
      const conversationId = await this.getOrCreateStaffConversation(data.memberUserId, data.clubId);

      if (!conversationId) {
        throw new Error('Failed to get conversation ID');
      }

      // Send the message
      const result = await this.messagingService.sendMessage({
        conversation_id: conversationId,
        content: data.content,
        message_type: 'text',
        message_data: {}
      });

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to send message');
      }

      return result.data;
    });
  }

  /**
   * Get staff conversation for a member in a specific club (if it exists)
   */
  async getMemberStaffConversation(memberUserId: string, clubId: string): Promise<Conversation | null> {
    return this.findStaffConversation(memberUserId, clubId);
  }

  /**
   * Batch-fetch staff conversations for multiple clubs in 2 queries instead of 2×N.
   * Accepts pre-fetched conversationIds to avoid redundant conversation_participants query.
   * Returns a Map of clubId → Conversation.
   */
  async getBatchMemberStaffConversations(
    memberUserId: string,
    clubIds: string[],
    prefetchedConversationIds?: string[],
  ): Promise<Map<string, Conversation>> {
    if (!clubIds.length) return new Map();

    let conversationIds = prefetchedConversationIds;
    if (!conversationIds) {
      const { data: participantConvs, error } = await this.supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', memberUserId)
        .eq('is_active', true);
      if (error || !participantConvs?.length) return new Map();
      conversationIds = participantConvs.map(p => p.conversation_id);
    }

    if (!conversationIds.length) return new Map();

    const descriptions = clubIds.map(id => `staff_support:club:${id}`);

    const { data: conversations, error: convError } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .in('description', descriptions);

    if (convError || !conversations?.length) return new Map();

    const result = new Map<string, Conversation>();
    for (const conv of conversations) {
      const clubId = conv.description?.replace('staff_support:club:', '');
      if (clubId) result.set(clubId, conv as Conversation);
    }
    return result;
  }

  /**
   * Fetch all support + coach conversations in 3 queries total (instead of 3× N+1).
   * Q1: conversation_participants (once, shared)
   * Q2: all matching conversations (staff + coach + strokon) in one IN query
   * Q3: all unread counts in one query
   * Q4: coach profiles in one query (parallel with Q3)
   */
  async getAllSupportConversations(memberUserId: string, clubIds: string[]): Promise<{
    staffMap: Map<string, Conversation>;
    coachConvs: Conversation[];
    strokonConv: Conversation | null;
    unreadCounts: Record<string, number>;
    coachProfiles: Record<string, { display_name: string | null; avatar_url: string | null }>;
  }> {
    const STROKON_USER_ID = '83f0046e-269c-4863-9f68-1fc753996bc4';

    // Q1: get all conversation IDs the user is in (once, shared across all lookups)
    const { data: participantConvs, error: participantError } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', memberUserId)
      .eq('is_active', true);

    if (participantError || !participantConvs?.length) {
      return { staffMap: new Map(), coachConvs: [], strokonConv: null, unreadCounts: {}, coachProfiles: {} };
    }

    const conversationIds = participantConvs.map(p => p.conversation_id);

    // Q2: fetch all support/coach/strokon conversations in one query using description patterns
    const staffDescriptions = clubIds.map(id => `staff_support:club:${id}`);
    const { data: allSupportConvs } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .or(
        [
          staffDescriptions.length ? `description.in.(${staffDescriptions.map(d => `"${d}"`).join(',')})` : null,
          'description.like.coaching:coach:%',
          `description.eq.strokon_support:founder`,
        ].filter(Boolean).join(',')
      );

    const staffMap = new Map<string, Conversation>();
    const coachConvs: Conversation[] = [];
    let strokonConv: Conversation | null = null;

    for (const conv of allSupportConvs || []) {
      if (conv.description?.startsWith('staff_support:club:')) {
        const clubId = conv.description.replace('staff_support:club:', '');
        staffMap.set(clubId, conv as Conversation);
      } else if (conv.description?.startsWith('coaching:coach:')) {
        coachConvs.push(conv as Conversation);
      } else if (conv.description === 'strokon_support:founder') {
        strokonConv = conv as Conversation;
      }
    }

    // Collect all support conv IDs for unread batch query
    const allSupportConvIds = [
      ...Array.from(staffMap.values()).map(c => c.id),
      ...coachConvs.map(c => c.id),
      ...(strokonConv ? [strokonConv.id] : []),
    ];

    // Q3 + Q4 in parallel: unread counts + coach profiles
    const coachUserIds = coachConvs
      .map(conv => this.getCoachUserIdFromConversation(conv))
      .filter((id): id is string => !!id);

    const [unreadData, profilesData] = await Promise.all([
      allSupportConvIds.length
        ? this.supabase
            .from('direct_messages')
            .select('conversation_id')
            .in('conversation_id', allSupportConvIds)
            .neq('sender_id', memberUserId)
            .eq('read', false)
            .then(({ data }) => data || [])
        : Promise.resolve([]),
      coachUserIds.length
        ? this.supabase
            .from('profiles')
            .select('id, display_name, avatar_url')
            .in('id', coachUserIds)
            .then(({ data }) => data || [])
        : Promise.resolve([]),
    ]);

    const unreadCounts: Record<string, number> = {};
    for (const msg of unreadData) {
      unreadCounts[msg.conversation_id] = (unreadCounts[msg.conversation_id] || 0) + 1;
    }

    const coachProfiles: Record<string, { display_name: string | null; avatar_url: string | null }> = {};
    for (const p of profilesData as any[]) {
      coachProfiles[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url };
    }

    return { staffMap, coachConvs, strokonConv, unreadCounts, coachProfiles };
  }

  /**
   * Get all staff conversations for a member (across all their clubs)
   */
  async getAllMemberStaffConversations(memberUserId: string): Promise<Conversation[]> {
    // Query 1: Get user's conversation IDs
    const { data: participantConvs, error: participantError } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', memberUserId)
      .eq('is_active', true);

    if (participantError || !participantConvs?.length) {
      return [];
    }

    const conversationIds = participantConvs.map(p => p.conversation_id);

    // Query 2: Find all staff_support conversations
    const { data: conversations, error: convError } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .like('description', 'staff_support:club:%');

    if (convError || !conversations) {
      return [];
    }

    return conversations as Conversation[];
  }

  /**
   * Check if conversation is a staff-member conversation
   */
  isStaffConversation(conversation: Conversation): boolean {
    const isGroup = conversation.conversation_type === 'group';
    const hasStaffDescription = conversation.description?.startsWith('staff_support:club:');
    // Also check legacy format for backwards compatibility in UI display
    const hasLegacyFormat = conversation.title === 'Staff Support' ||
                           conversation.description === 'Support conversation for member';
    return isGroup && (hasStaffDescription || hasLegacyFormat);
  }

  /**
   * Extract club ID from staff conversation description
   */
  getClubIdFromConversation(conversation: Conversation): string | null {
    if (!conversation.description?.startsWith('staff_support:club:')) {
      return null;
    }
    return conversation.description.replace('staff_support:club:', '');
  }

  /**
   * Get all staff user IDs for a club
   */
  private async getClubStaffUserIds(clubId: string): Promise<string[]> {
    const { data, error } = await this.supabase
      .from('user_club_memberships')
      .select('user_id, role')
      .eq('club_id', clubId)
      .in('role', ['admin', 'owner']);

    if (error) {
      throw error;
    }

    return data?.map(row => row.user_id) || [];
  }

  // ============================================================================
  // Coach Conversation Methods
  // ============================================================================

  /**
   * Find existing coach conversation for a player with a specific coach.
   * Description format: "coaching:coach:{coachUserId}:player:{playerUserId}" (new)
   * or legacy format: "coaching:coach:{coachUserId}" (old - requires participant verification)
   */
  private async findCoachConversation(playerUserId: string, coachUserId: string): Promise<Conversation | null> {
    // Query 1: Get PLAYER's conversation IDs
    const { data: playerConvs, error: playerError } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', playerUserId)
      .eq('is_active', true);

    if (playerError || !playerConvs?.length) {
      return null;
    }

    const playerConversationIds = playerConvs.map(p => p.conversation_id);

    // Try new format first: coaching:coach:{coachUserId}:player:{playerUserId}
    const newDescription = `coaching:coach:${coachUserId}:player:${playerUserId}`;
    const { data: newFormatConv } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', playerConversationIds)
      .eq('description', newDescription)
      .limit(1)
      .maybeSingle();

    if (newFormatConv) {
      return newFormatConv as Conversation;
    }

    // Fallback to legacy format: coaching:coach:{coachUserId}
    // But we must verify BOTH player AND coach are participants
    const legacyDescription = `coaching:coach:${coachUserId}`;
    const { data: legacyConvs } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', playerConversationIds)
      .eq('description', legacyDescription);

    if (!legacyConvs?.length) {
      return null;
    }

    // For each candidate, verify the coach is ALSO a participant
    for (const conv of legacyConvs) {
      const { data: coachParticipant } = await this.supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conv.id)
        .eq('user_id', coachUserId)
        .eq('is_active', true)
        .maybeSingle();

      // Only return if BOTH player and coach are participants
      // This prevents returning Bus bus's conversation when looking for Theo's
      if (coachParticipant) {
        // Additional check: for self-coaching (player === coach), ensure exactly 2 participants
        // or that there's only one unique participant
        if (playerUserId === coachUserId) {
          const { count } = await this.supabase
            .from('conversation_participants')
            .select('user_id', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('is_active', true);

          // Self-coaching conversation should have exactly 1 unique participant (the coach/player)
          // But our schema might have 2 rows with same user_id, so check if count <= 2
          if (count && count <= 2) {
            // Get distinct participants
            const { data: allParticipants } = await this.supabase
              .from('conversation_participants')
              .select('user_id')
              .eq('conversation_id', conv.id)
              .eq('is_active', true);

            const uniqueParticipants = new Set(allParticipants?.map(p => p.user_id) || []);
            // For self-coaching, should only have 1 unique participant
            if (uniqueParticipants.size === 1 && uniqueParticipants.has(playerUserId)) {
              return conv as Conversation;
            }
          }
        } else {
          // Normal case: player !== coach, just return the conversation
          return conv as Conversation;
        }
      }
    }

    return null;
  }

  /**
   * Get or create a coaching conversation between a player and their coach.
   * Uses description "coaching:coach:{coachUserId}:player:{playerUserId}" for exact matching.
   * Title: "Coach {CoachName}"
   * Type: group (to distinguish from regular direct messages, same as staff)
   */
  async getOrCreateCoachConversation(playerUserId: string, coachUserId: string, coachName?: string): Promise<string> {
    // Check if conversation already exists
    const existingConv = await this.findCoachConversation(playerUserId, coachUserId);

    if (existingConv) {
      return existingConv.id;
    }

    // Get current user to avoid duplicate entries
    const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get coach display name if not provided
    let displayName = coachName;
    if (!displayName) {
      const { data: profile } = await this.supabase
        .from('profiles')
        .select('display_name')
        .eq('id', coachUserId)
        .single();
      displayName = profile?.display_name || 'Coach';
    }

    // Remove current user from participant_ids to avoid duplicates
    // (createConversation automatically adds the current user as admin)
    const participantIds = [playerUserId, coachUserId].filter(id => id !== user.id);

    const result = await this.messagingService.createConversation({
      conversation_type: 'group',
      participant_ids: participantIds,
      title: `Coach ${displayName}`,
      description: `coaching:coach:${coachUserId}:player:${playerUserId}` // Unique per player-coach pair
    });

    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to create coaching conversation');
    }

    return result.data?.data?.id || result.data?.id;
  }

  /**
   * Get coaching conversation if it exists (without creating)
   */
  async getCoachConversation(playerUserId: string, coachUserId: string): Promise<Conversation | null> {
    return this.findCoachConversation(playerUserId, coachUserId);
  }

  /**
   * Get all coaching conversations for a player
   */
  async getAllPlayerCoachConversations(playerUserId: string): Promise<Conversation[]> {
    // Query 1: Get user's conversation IDs
    const { data: participantConvs, error: participantError } = await this.supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', playerUserId)
      .eq('is_active', true);

    if (participantError || !participantConvs?.length) {
      return [];
    }

    const conversationIds = participantConvs.map(p => p.conversation_id);

    // Query 2: Find all coaching conversations
    const { data: conversations, error: convError } = await this.supabase
      .from('conversations')
      .select('*')
      .in('id', conversationIds)
      .like('description', 'coaching:coach:%');

    if (convError || !conversations) {
      return [];
    }

    return conversations as Conversation[];
  }

  /**
   * Check if conversation is a coaching conversation
   */
  isCoachConversation(conversation: Conversation): boolean {
    return !!conversation.description?.startsWith('coaching:coach:');
  }

  /**
   * Extract coach user ID from coaching conversation description
   * Format: coaching:coach:{coachUserId}:player:{playerUserId}
   */
  getCoachUserIdFromConversation(conversation: Conversation): string | null {
    if (!conversation.description?.startsWith('coaching:coach:')) {
      return null;
    }
    // Description format: coaching:coach:{coachUserId}:player:{playerUserId}
    const match = conversation.description.match(/^coaching:coach:([^:]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get all clubs where the user is a member (not staff)
   */
  async getMemberClubs(memberUserId: string): Promise<Array<{ clubId: string; clubName: string }>> {
    const { data, error } = await this.supabase
      .from('user_club_memberships')
      .select('club_id, clubs(name)')
      .eq('user_id', memberUserId)
      .is('staff_role', null); // Member, not staff

    if (error || !data) {
      return [];
    }

    return data.map(row => ({
      clubId: row.club_id,
      clubName: (row.clubs as any)?.name || 'Unknown Club'
    }));
  }

  /**
   * @deprecated Use getMemberClubs instead for multi-club support
   * Get member's club membership info (returns first club only)
   */
  async getMemberClubInfo(memberUserId: string): Promise<{ clubId: string; membershipId: string } | null> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('user_club_memberships')
        .select('club_id, id')
        .eq('user_id', memberUserId)
        .is('staff_role', null) // Member, not staff
        .limit(1)
        .single();

      if (error || !data) {
        return null;
      }

      return {
        clubId: data.club_id,
        membershipId: data.id
      };
    });
  }
}

export const staffMemberMessagingService = new StaffMemberMessagingService();
